// Keep the avatar's own face when Meshy retextures it.
//
// A retexture repaints the WHOLE texture, so an outfit change also gave the
// avatar a different face and skin tone. The head region of the texture is
// found from the rig — triangles skinned to the head bones — and the original
// pixels are put back there, so only the clothes come from Meshy.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { getDRACOLoader } from '../utils/loaderConfig';

const HEAD_BONE = /^(head|head_end|headfront|headtop_end)$/i;

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('look image failed to decode')); };
    img.src = url;
  });
}

/** Per-vertex "is head" from skin weights, or top of the body if unrigged. */
function headVertices(mesh: THREE.Mesh): Float32Array | null {
  const geo = mesh.geometry;
  const n = geo.attributes.position.count;
  const out = new Float32Array(n);
  const skinned = (mesh as THREE.SkinnedMesh).isSkinnedMesh ? (mesh as THREE.SkinnedMesh) : null;
  const idx = geo.attributes.skinIndex, wt = geo.attributes.skinWeight;
  if (skinned && idx && wt) {
    const heads = new Set<number>();
    skinned.skeleton.bones.forEach((b, i) => { if (HEAD_BONE.test(b.name)) heads.add(i); });
    if (heads.size) {
      for (let v = 0; v < n; v++) {
        let w = 0;
        for (let k = 0; k < 4; k++) if (heads.has(idx.getComponent(v, k))) w += wt.getComponent(v, k);
        out[v] = w;
      }
      return out;
    }
  }
  // No head bone: the top ~13% of the body's height.
  geo.computeBoundingBox();
  const box = geo.boundingBox;
  if (!box) return null;
  const cut = box.max.y - (box.max.y - box.min.y) * 0.13;
  for (let v = 0; v < n; v++) out[v] = geo.attributes.position.getY(v) >= cut ? 1 : 0;
  return out;
}

/**
 * Returns the look texture with the model's original head pixels restored.
 * Throws if the model has no usable texture/UVs (callers fall back to the raw
 * look).
 */
export async function preserveOriginalHead(modelUrl: string, lookBlob: Blob): Promise<Blob> {
  const loader = new GLTFLoader();
  try { loader.setDRACOLoader(getDRACOLoader()); } catch { /* draco optional */ }
  const gltf = await loader.loadAsync(modelUrl);

  let mesh: THREE.Mesh | null = null;
  gltf.scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!mesh && m.isMesh && (m.material as THREE.MeshStandardMaterial)?.map && m.geometry.attributes.uv) mesh = m;
  });
  if (!mesh) throw new Error('model has no textured mesh');
  const target = mesh as THREE.Mesh;
  const original = (target.material as THREE.MeshStandardMaterial).map!.image as CanvasImageSource & { width: number; height: number };
  const look = await loadImage(lookBlob);
  const W = look.naturalWidth, H = look.naturalHeight;

  // Mask of head triangles in UV space (glTF UVs: origin top-left, no flip).
  const mask = document.createElement('canvas');
  mask.width = W; mask.height = H;
  const mctx = mask.getContext('2d')!;
  mctx.fillStyle = '#fff'; mctx.strokeStyle = '#fff';
  mctx.lineWidth = Math.max(2, W / 512); // close UV seams / gutters
  mctx.lineJoin = 'round';
  const head = headVertices(target);
  if (!head) throw new Error('could not find the head');
  const uv = target.geometry.attributes.uv;
  const index = target.geometry.index;
  const tris = index ? index.count / 3 : uv.count / 3;
  let drawn = 0;
  for (let t = 0; t < tris; t++) {
    const a = index ? index.getX(t * 3) : t * 3, b = index ? index.getX(t * 3 + 1) : t * 3 + 1, c = index ? index.getX(t * 3 + 2) : t * 3 + 2;
    if ((head[a] + head[b] + head[c]) / 3 < 0.5) continue;
    mctx.beginPath();
    mctx.moveTo(uv.getX(a) * W, uv.getY(a) * H);
    mctx.lineTo(uv.getX(b) * W, uv.getY(b) * H);
    mctx.lineTo(uv.getX(c) * W, uv.getY(c) * H);
    mctx.closePath();
    mctx.fill(); mctx.stroke();
    drawn++;
  }
  if (!drawn) throw new Error('no head triangles');

  // Original head pixels, cut out by the mask…
  const headLayer = document.createElement('canvas');
  headLayer.width = W; headLayer.height = H;
  const hctx = headLayer.getContext('2d')!;
  hctx.drawImage(original, 0, 0, W, H);
  hctx.globalCompositeOperation = 'destination-in';
  hctx.drawImage(mask, 0, 0);

  // …laid over the new look, whose bare skin (arms, hands) is first shifted to
  // the original skin tone so the body matches the restored face.
  const out = document.createElement('canvas');
  out.width = W; out.height = H;
  const octx = out.getContext('2d', { willReadFrequently: true })!;
  octx.drawImage(look, 0, 0);
  try { matchSkinTone(octx, original, mask, W, H); } catch (e) { console.warn('[looks] skin tone match skipped', e); }
  octx.drawImage(headLayer, 0, 0);

  gltf.scene.traverse((o: any) => {
    o.geometry?.dispose?.();
    (Array.isArray(o.material) ? o.material : o.material ? [o.material] : []).forEach((m: any) => {
      for (const k in m) if (m[k]?.isTexture) m[k].dispose();
      m.dispose?.();
    });
  });

  const blob = await new Promise<Blob | null>((res) => out.toBlob(res, 'image/png'));
  if (!blob) throw new Error('could not encode the look');
  return blob;
}

/** Skin-like: warm hue, moderate saturation, not too dark (excludes hair). */
function isSkin(r: number, g: number, b: number): boolean {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max < 50 || max - min < 18) return false;
  if (!(r >= g && g >= b * 0.85)) return false; // red ≥ green ≥ ~blue
  const s = (max - min) / max;
  return s > 0.15 && s < 0.8;
}

/**
 * Shift the look's bare skin to the original avatar's skin colour. Reference
 * colours come from the face (head mask) of each texture; look pixels close
 * to the look's own skin colour are scaled per channel, with a soft falloff so
 * similar-coloured clothing at the edge of the range is barely touched.
 */
function matchSkinTone(ctx: CanvasRenderingContext2D, original: CanvasImageSource, mask: HTMLCanvasElement, W: number, H: number) {
  const look = ctx.getImageData(0, 0, W, H);
  const o = document.createElement('canvas');
  o.width = W; o.height = H;
  const octx = o.getContext('2d', { willReadFrequently: true })!;
  octx.drawImage(original, 0, 0, W, H);
  const orig = octx.getImageData(0, 0, W, H).data;
  const m = mask.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, W, H).data;
  const L = look.data;

  // Skin colour of each texture = its most common skin-like colour on the BODY
  // (outside the head mask). The head is mostly hair, which skews any average
  // taken there; bare hands, neck and arms are what actually need matching.
  const so = dominantSkin(orig, m);
  const sl = dominantSkin(L, m);
  if (!so || !sl) return; // no bare skin to compare
  const gain = so.map((v, k) => v / Math.max(1, sl[k]));
  if (gain.every((g) => Math.abs(g - 1) < 0.04)) return; // already matches

  const NEAR = 60, FAR = 95; // RGB distance from the look's skin colour
  for (let i = 0; i < L.length; i += 4) {
    if (m[i + 3] > 0) continue; // the head is replaced wholesale anyway
    const r = L[i], g = L[i + 1], b = L[i + 2];
    if (!isSkin(r, g, b)) continue;
    const d = Math.hypot(r - sl[0], g - sl[1], b - sl[2]);
    if (d > FAR) continue;
    const w = d <= NEAR ? 1 : 1 - (d - NEAR) / (FAR - NEAR);
    L[i] = Math.min(255, r + (r * gain[0] - r) * w);
    L[i + 1] = Math.min(255, g + (g * gain[1] - g) * w);
    L[i + 2] = Math.min(255, b + (b * gain[2] - b) * w);
  }
  ctx.putImageData(look, 0, 0);
}

/**
 * Most common skin-like colour outside the head mask: a 16-level RGB
 * histogram picks the peak, then the mean of nearby pixels refines it.
 */
function dominantSkin(px: Uint8ClampedArray, mask: Uint8ClampedArray): number[] | null {
  const bins = new Uint32Array(16 * 16 * 16);
  let total = 0;
  for (let i = 0; i < px.length; i += 16) {
    if (mask[i + 3] > 0) continue;
    const r = px[i], g = px[i + 1], b = px[i + 2];
    if (!isSkin(r, g, b)) continue;
    bins[((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)]++;
    total++;
  }
  if (total < 200) return null;
  let best = 0;
  for (let k = 1; k < bins.length; k++) if (bins[k] > bins[best]) best = k;
  const c = [((best >> 8) << 4) + 8, (((best >> 4) & 15) << 4) + 8, ((best & 15) << 4) + 8];
  const sum = [0, 0, 0];
  let n = 0;
  for (let i = 0; i < px.length; i += 16) {
    if (mask[i + 3] > 0) continue;
    const r = px[i], g = px[i + 1], b = px[i + 2];
    if (Math.hypot(r - c[0], g - c[1], b - c[2]) > 40 || !isSkin(r, g, b)) continue;
    sum[0] += r; sum[1] += g; sum[2] += b; n++;
  }
  return n ? sum.map((v) => v / n) : c;
}

/** Looks that asked for new hair or makeup must keep Meshy's head. */
export function wantsHeadChange(displayPrompt: string | null | undefined): boolean {
  return (displayPrompt || '').split(' · ').some((s) => / hair$/i.test(s.trim()) || /^makeup:/i.test(s.trim()));
}
